import { CheckCircleFilled, SyncOutlined } from "@ant-design/icons";
import { Input, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ReactComponent as InstagramIcon } from "assets/images/instagram-icon.svg";
import { ReactComponent as TikTokIcon } from "assets/images/tiktok-icon.svg";
import {
  generateLeadSubmissionAIBrief,
  getLeadSubmissionCollabs,
  type LeadSubmissionCollab,
  type LeadSubmissionCollabsResponse,
  updateLeadSubmissionProgress,
} from "services/SauceService";
import { getSelfCampaignQueryParams } from "./queryParams";
import {
  getSelfServeCampaignCachedCollabs,
  getSelfServeCampaignDraft,
  saveSelfServeCampaignCachedCollabs,
  saveSelfServeCampaignDesignStepDraft,
} from "./storage";
import {
  SelfServeCampaignBusinessType,
  SelfServeCampaignPlatform,
  SelfServeCampaignPromotionType,
  SelfServeCampaignTone,
} from "./types";
import SelfCampaignPageLayout from "./components/SelfCampaignPageLayout";
import SelfCampaignPrimaryCta from "./components/SelfCampaignPrimaryCta";
import SelfCampaignProgress from "./components/SelfCampaignProgress";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import "./SelfCampaignDesign.scss";

const { TextArea } = Input;

const TONE_OPTIONS: { value: SelfServeCampaignTone; label: string }[] = [
  { value: "fun", label: "Fun" },
  { value: "premium", label: "Premium" },
  { value: "trendy", label: "Trendy" },
  { value: "family_friendly", label: "Family-friendly" },
];

const BUSINESS_TYPE_LABELS: Record<SelfServeCampaignBusinessType, string> = {
  restaurant_food_beverage: "Restaurant",
  cafe_coffee_shop: "Cafe",
  bar_nightlife: "Bar / Nightlife",
  hotel_resort: "Hotel / Resort",
  travel_tourism: "Travel / Tourism",
  event_venue: "Event Venue",
  beauty_skincare: "Beauty / Skincare",
  fitness_wellness: "Fitness / Wellness",
  fashion_apparel: "Fashion / Apparel",
  retail_local_shop: "Retail",
  consumer_brand: "Consumer Brand",
  e_commerce: "E-commerce",
  entertainment_attractions: "Entertainment",
  arts_culture: "Arts / Culture",
  other: "Local Business",
};

type CreatorPreview = {
  handle: string;
  followers: string;
  badge: string;
  image: string;
  avatar: string;
};

const CREATOR_PREVIEW_SEED: Omit<CreatorPreview, "badge">[] = [
  {
    handle: "@foodie_la",
    followers: "28.5K followers",
    image:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/0318d5ad22-38ee46bb665419929b92.png",
    avatar:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg",
  },
  {
    handle: "@la_eats",
    followers: "42.1K followers",
    image:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/42603e040e-77cc68987fda9892d0ab.png",
    avatar:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg",
  },
  {
    handle: "@tastyla",
    followers: "18.3K followers",
    image:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/c9e3a25a4d-f7f4987c3ea7ccf9c063.png",
    avatar:
      "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-5.jpg",
  },
] satisfies Omit<CreatorPreview, "badge">[];

const DEFAULT_CREATOR_COUNT = 493;

const resolveLocationContext = (
  storeAddress?: string,
): { city: string; neighborhood: string } => {
  const normalized = (storeAddress ?? "").trim();
  if (!normalized) {
    return { city: "your city", neighborhood: "your neighborhood" };
  }

  const parts = normalized
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  const city = parts.length >= 3 ? parts[parts.length - 3] : parts[0];
  const neighborhoodCandidate =
    parts.length >= 4 ? parts[parts.length - 4] : parts[0];
  const withoutBusinessPrefix = neighborhoodCandidate.includes(" - ")
    ? neighborhoodCandidate.split(" - ").slice(1).join(" - ").trim()
    : neighborhoodCandidate;
  const neighborhood = /\d/.test(withoutBusinessPrefix)
    ? "your neighborhood"
    : withoutBusinessPrefix || "your neighborhood";

  return {
    city: city || "your city",
    neighborhood,
  };
};

const buildFallbackCreatorPreview = (
  businessTypeLabel: string,
  cityLabel: string,
  neighborhoodLabel: string,
): CreatorPreview[] => {
  const badgeLabels = [
    `Popular with ${cityLabel} ${businessTypeLabel}`,
    `${cityLabel} audience`,
    `${businessTypeLabel} specialist`,
  ];

  return CREATOR_PREVIEW_SEED.map((creator, index) => ({
    ...creator,
    badge: badgeLabels[index % badgeLabels.length],
  }));
};

const formatFollowersCount = (count: number): string => {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1).replace(".0", "")}M followers`;
  }

  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1).replace(".0", "")}K followers`;
  }

  return `${count} followers`;
};

const normalizeCreatorHandle = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return trimmed;
  }

  if (trimmed.startsWith("@")) {
    return trimmed;
  }

  if (/\s/.test(trimmed)) {
    return trimmed;
  }

  return `@${trimmed}`;
};

const normalizeCreatorPreview = (
  collabs: LeadSubmissionCollab[],
  fallbackPreview: CreatorPreview[],
): CreatorPreview[] => {
  if (collabs.length === 0) {
    return fallbackPreview;
  }

  return collabs.map((collab, index) => {
    const fallback = fallbackPreview[index % fallbackPreview.length];
    const handle = collab.creatorUsername
      ? normalizeCreatorHandle(collab.creatorUsername)
      : fallback.handle;
    const followers =
      typeof collab.followers === "number" && collab.followers > 0
        ? formatFollowersCount(collab.followers)
        : fallback.followers;

    return {
      handle,
      followers,
      badge: fallback.badge,
      image: collab.postCoverImg || fallback.image,
      avatar: collab.avatarUrl || fallback.avatar,
    };
  });
};

const getCreatorCount = (
  payload: LeadSubmissionCollabsResponse,
  fallbackCount: number,
): number => {
  if (
    typeof payload.creatorsAmount === "number" &&
    Number.isFinite(payload.creatorsAmount) &&
    payload.creatorsAmount > 0
  ) {
    return payload.creatorsAmount;
  }

  if (Array.isArray(payload.topCollabs) && payload.topCollabs.length > 0) {
    return payload.topCollabs.length;
  }

  return fallbackCount;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  return value as Record<string, unknown>;
};

const asTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const extractAIBrief = (payload: unknown): string | undefined => {
  const payloadRecord = asRecord(payload);
  const nestedRecord = asRecord(payloadRecord?.data);
  const candidates = [
    payloadRecord?.brief,
    payloadRecord?.generatedBrief,
    payloadRecord?.aiBrief,
    payloadRecord?.text,
    nestedRecord?.brief,
    nestedRecord?.generatedBrief,
    nestedRecord?.aiBrief,
    nestedRecord?.text,
  ];

  for (const candidate of candidates) {
    const resolved = asTrimmedString(candidate);
    if (resolved) {
      return resolved;
    }
  }

  return undefined;
};

const extractBackendSummary = (
  leadSubmission: Record<string, unknown> | undefined,
): string | undefined => {
  if (!leadSubmission) {
    return undefined;
  }

  const nestedRecord = asRecord(leadSubmission.data);
  const candidates = [
    leadSubmission.summary,
    leadSubmission.businessSummary,
    leadSubmission.description,
    leadSubmission.businessDescription,
    leadSubmission.brief,
    nestedRecord?.summary,
    nestedRecord?.businessSummary,
    nestedRecord?.description,
    nestedRecord?.businessDescription,
    nestedRecord?.brief,
  ];

  for (const candidate of candidates) {
    const resolved = asTrimmedString(candidate);
    if (resolved) {
      return resolved;
    }
  }

  return undefined;
};

const buildDefaultBrief = (businessName?: string): string => {
  const resolvedName = (businessName ?? "").trim() || "your business";
  return `Showcase ${resolvedName}'s unique experience and atmosphere. Highlight what makes your business stand out and attract local creators.`;
};

const mapToneToApiValue = (
  tone: SelfServeCampaignTone,
): "fun" | "premium" | "trendy" | "family-friendly" => {
  if (tone === "family_friendly") {
    return "family-friendly";
  }

  return tone;
};

const SelfCampaignDesign: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { businessName } = useMemo(() => {
    return getSelfCampaignQueryParams(location.search);
  }, [location.search]);

  const draft = useMemo(() => {
    return getSelfServeCampaignDraft();
  }, []);
  const leadSubmissionId = draft?.meta?.leadSubmissionId;
  const storeAddress = draft?.createStep?.storeAddress;
  const isFoodAndBeverage =
    draft?.createStep?.businessType === "restaurant_food_beverage";
  const businessTypeLabel = draft?.createStep?.businessType
    ? BUSINESS_TYPE_LABELS[draft.createStep.businessType]
    : "Local Business";
  const { city: cityLabel, neighborhood: neighborhoodLabel } = useMemo(() => {
    return resolveLocationContext(storeAddress);
  }, [storeAddress]);
  const fallbackCreatorPreview = useMemo(() => {
    return buildFallbackCreatorPreview(
      businessTypeLabel,
      cityLabel,
      neighborhoodLabel,
    );
  }, [businessTypeLabel, cityLabel, neighborhoodLabel]);
  const backendSummary = useMemo(() => {
    const leadSubmission = asRecord(draft?.meta?.leadSubmission);
    return extractBackendSummary(leadSubmission ?? undefined);
  }, [draft?.meta?.leadSubmission]);
  const defaultBrief = useMemo(() => {
    return (
      backendSummary ||
      buildDefaultBrief(businessName || draft?.meta?.businessName)
    );
  }, [backendSummary, businessName, draft?.meta?.businessName]);

  const [promotionType, setPromotionType] =
    useState<SelfServeCampaignPromotionType>(
      draft?.designStep?.promotionType ?? "hybrid",
    );
  const [platform, setPlatform] = useState<SelfServeCampaignPlatform>(
    draft?.designStep?.platform ?? "instagram_tiktok",
  );
  const [brief, setBrief] = useState<string>(
    draft?.designStep?.brief ?? defaultBrief,
  );
  const [tone, setTone] = useState<SelfServeCampaignTone>(
    draft?.designStep?.tone ?? "fun",
  );
  const [creatorPreview, setCreatorPreview] = useState<CreatorPreview[]>([]);
  const [creatorCount, setCreatorCount] = useState<number | null>(null);
  const [loadingNearbyCollabs, setLoadingNearbyCollabs] = useState(true);
  const [loadingAIBrief, setLoadingAIBrief] = useState(false);
  const [savingStepProgress, setSavingStepProgress] = useState(false);
  const [designError, setDesignError] = useState<string | null>(null);

  useEffect(() => {
    if (leadSubmissionId) {
      return;
    }

    navigate(
      {
        pathname: "/self-campaign",
        search: location.search,
      },
      { replace: true },
    );
  }, [leadSubmissionId, location.search, navigate]);

  useEffect(() => {
    let isMounted = true;

    const loadNearbyCollabs = async () => {
      if (!leadSubmissionId) {
        return;
      }

      const cachedCollabs = getSelfServeCampaignCachedCollabs(
        leadSubmissionId,
        storeAddress,
      );
      if (cachedCollabs) {
        const hasTopCollabs = cachedCollabs.topCollabs.length > 0;

        setDesignError(null);
        setCreatorPreview(
          hasTopCollabs
            ? normalizeCreatorPreview(
                cachedCollabs.topCollabs,
                fallbackCreatorPreview,
              )
            : fallbackCreatorPreview,
        );
        setCreatorCount(getCreatorCount(cachedCollabs, DEFAULT_CREATOR_COUNT));
        setLoadingNearbyCollabs(false);
        return;
      }

      try {
        setLoadingNearbyCollabs(true);
        setDesignError(null);
        const response = await getLeadSubmissionCollabs(leadSubmissionId);

        if (!isMounted) {
          return;
        }

        const topCollabs = Array.isArray(response.topCollabs)
          ? response.topCollabs
          : [];
        const hasTopCollabs = topCollabs.length > 0;

        setCreatorPreview(
          hasTopCollabs
            ? normalizeCreatorPreview(topCollabs, fallbackCreatorPreview)
            : fallbackCreatorPreview,
        );
        setCreatorCount(getCreatorCount(response, DEFAULT_CREATOR_COUNT));
        saveSelfServeCampaignCachedCollabs(
          leadSubmissionId,
          response,
          storeAddress,
        );
      } catch (_error) {
        if (isMounted) {
          setCreatorPreview([]);
          setCreatorCount(null);
          setDesignError("Could not load nearby collabs right now.");
        }
      } finally {
        if (isMounted) {
          setLoadingNearbyCollabs(false);
        }
      }
    };

    void loadNearbyCollabs();

    return () => {
      isMounted = false;
    };
  }, [fallbackCreatorPreview, leadSubmissionId, storeAddress]);

  const canContinue = brief.trim().length > 0 && Boolean(leadSubmissionId);

  const onContinue = async () => {
    if (!leadSubmissionId) {
      return;
    }

    const normalizedBrief = brief.trim();

    if (!normalizedBrief) {
      return;
    }

    try {
      setDesignError(null);
      setSavingStepProgress(true);

      await updateLeadSubmissionProgress(leadSubmissionId, {
        step: 2,
        campaignType: promotionType === "hybrid" ? "hybrid" : "credit",
        socialMediaTarget: platform,
        brief: normalizedBrief,
      });
    } catch (_error) {
      setDesignError("We could not save your progress. Please try again.");
      setSavingStepProgress(false);
      return;
    }

    saveSelfServeCampaignDesignStepDraft({
      promotionType,
      platform,
      brief: normalizedBrief,
      tone,
    });

    navigate({
      pathname: "/self-campaign/activate",
      search: location.search,
    });

    setSavingStepProgress(false);
  };

  const onRegenerateBrief = async () => {
    if (!leadSubmissionId) {
      return;
    }

    try {
      setLoadingAIBrief(true);
      setDesignError(null);

      const response = await generateLeadSubmissionAIBrief(leadSubmissionId, {
        tone: mapToneToApiValue(tone),
      });

      const generatedBrief = extractAIBrief(response);

      if (!generatedBrief) {
        setDesignError("AI could not generate a brief right now.");
        setLoadingAIBrief(false);
        return;
      }

      setBrief(generatedBrief);
      setLoadingAIBrief(false);
    } catch (_error) {
      setDesignError("AI could not generate a brief right now.");
      setLoadingAIBrief(false);
    }
  };

  return (
    <SelfCampaignPageLayout
      rootClassName="self-campaign-design"
      variant="design"
      footer={
        <SelfCampaignPrimaryCta
          label="Continue to Activate"
          mode="responsive-footer"
          onClick={onContinue}
          disabled={!canContinue || savingStepProgress}
        />
      }
    >
      <SelfCampaignProgress
        title="Design your campaign"
        activeStep={3}
        variant="design"
      />

      <section className="self-campaign-design__creators-card">
        <div className="self-campaign-design__creators-header">
          <h2>
            {loadingNearbyCollabs
              ? "Loading nearby creators..."
              : `${creatorCount ?? DEFAULT_CREATOR_COUNT} Local creators in ${cityLabel}`}
          </h2>
          <p>{`Tailored for ${businessTypeLabel}`}</p>
        </div>
        <div className="self-campaign-design__creators-list">
          {loadingNearbyCollabs ? (
            <div className="self-campaign-design__creators-loading">
              <div className="self-campaign-design__creator-skeleton" />
              <div className="self-campaign-design__creator-skeleton" />
              <div className="self-campaign-design__creator-skeleton" />
              <div className="self-campaign-design__creator-more self-campaign-design__creator-more--skeleton" />
            </div>
          ) : (
            <>
              {creatorPreview.map((creator) => (
                <article
                  key={creator.handle}
                  className="self-campaign-design__creator-item"
                >
                  <img
                    src={creator.image}
                    alt={creator.handle}
                    className="self-campaign-design__creator-cover"
                  />
                  <div className="self-campaign-design__creator-overlay" />
                  {/* <span className="self-campaign-design__creator-badge">
                    {creator.badge}
                  </span> */}
                  <div className="self-campaign-design__creator-footer">
                    <img src={creator.avatar} alt={creator.handle} />
                    <div>
                      <p>{creator.handle}</p>
                      <span>{creator.followers}</span>
                    </div>
                  </div>
                </article>
              ))}
              {creatorPreview.length > 0 ? (
                <div className="self-campaign-design__creator-more">
                  <FontAwesomeIcon icon={faArrowRight} />
                  <p>Activate to see more</p>
                </div>
              ) : (
                <Typography.Text className="self-campaign-design__creators-empty">
                  No creators available right now.
                </Typography.Text>
              )}
            </>
          )}
        </div>
      </section>

      {designError ? (
        <Typography.Text
          type="danger"
          className="self-campaign-design__status-message"
        >
          {designError}
        </Typography.Text>
      ) : null}

      <section className="self-campaign-design__section">
        <div className="self-campaign-design__section-heading">
          <h3>
            How would you like creators to promote your {businessTypeLabel}?
          </h3>
          <p>
            The more you offer, the more creators apply and the more buzz you
            generate.
          </p>
        </div>
        <div className="self-campaign-design__promotion-grid">
          <button
            type="button"
            className={`self-campaign-design__promotion-card ${
              promotionType === "experience_credit" ? "selected" : ""
            }`}
            onClick={() => setPromotionType("experience_credit")}
          >
            <h4>Experience Credit</h4>
            <span className="chip">Lower Cost</span>
            <p>
              {isFoodAndBeverage
                ? "Invite creators to enjoy a dining experience in exchange for content."
                : "Invite creators to enjoy an experience in exchange for content."}
            </p>
            <div className="price-box">
              <small>Per creator</small>
              <strong>$75-$250</strong>
            </div>
            <ul>
              <li>Lower cost option</li>
              <li>Good for basic exposure</li>
            </ul>
          </button>

          <button
            type="button"
            className={`self-campaign-design__promotion-card self-campaign-design__promotion-card--featured ${
              promotionType === "hybrid" ? "selected" : ""
            }`}
            onClick={() => setPromotionType("hybrid")}
          >
            <span className="top-chip">Most Popular</span>
            <h4>Hybrid</h4>
            <span className="chip chip--primary">Best Results</span>
            <p>
              {isFoodAndBeverage
                ? "Combine dining experience + paid collaboration for stronger results."
                : "Combine experience credit + paid collaboration for stronger results."}
            </p>
            <div className="price-box">
              <small>Per creator</small>
              <strong>$75-$250 + $100-$250</strong>
            </div>
            <ul>
              <li>More creator applications</li>
              <li>Higher quality content</li>
              <li>Greater buzz and reach</li>
            </ul>
            <div className="insight">
              3x more creator applications on average
            </div>
          </button>
        </div>
      </section>

      <section className="self-campaign-design__section">
        <div className="self-campaign-design__section-heading">
          <h3>Where should this run?</h3>
        </div>
        <div className="self-campaign-design__platform-grid">
          <button
            type="button"
            className={`self-campaign-design__platform-card ${
              platform === "instagram" ? "selected" : ""
            }`}
            onClick={() => setPlatform("instagram")}
          >
            <div className="icon-wrap icon-wrap--instagram">
              <InstagramIcon />
            </div>
            <div>
              <h4>Instagram Only</h4>
              <p>Stories and Reels</p>
            </div>
            <small>Best for visual storytelling</small>
          </button>

          <button
            type="button"
            className={`self-campaign-design__platform-card ${
              platform === "instagram_tiktok" ? "selected" : ""
            }`}
            onClick={() => setPlatform("instagram_tiktok")}
          >
            <span className="top-chip">Popular</span>
            <div className="icon-wrap icon-wrap--combo">
              <InstagramIcon />
              <TikTokIcon />
            </div>
            <div>
              <h4>Instagram / TikTok</h4>
              <p>Maximum reach</p>
            </div>
            <small>2x platform exposure and diverse audience</small>
          </button>
        </div>
      </section>

      <section className="self-campaign-design__section">
        <div className="self-campaign-design__section-heading self-campaign-design__section-heading--row">
          <h3>Campaign Brief</h3>
          <button
            type="button"
            className="self-campaign-design__ai-btn"
            onClick={onRegenerateBrief}
            disabled={loadingAIBrief || !leadSubmissionId}
          >
            <SyncOutlined />
            {loadingAIBrief ? "Generating..." : "Regenerate with AI"}
          </button>
        </div>
        <TextArea
          value={brief}
          onChange={(event) => setBrief(event.target.value)}
          rows={5}
        />
        <div className="self-campaign-design__tone-row">
          <span>Tone:</span>
          {TONE_OPTIONS.map((toneOption) => (
            <button
              key={toneOption.value}
              type="button"
              onClick={() => setTone(toneOption.value)}
              className={`self-campaign-design__tone-btn ${
                tone === toneOption.value ? "active" : ""
              }`}
            >
              {toneOption.label}
            </button>
          ))}
        </div>
      </section>

      <section className="self-campaign-design__summary">
        <CheckCircleFilled />
        <Typography.Text>
          Campaign for <strong>{businessName || "your business"}</strong> is
          ready for activation.
        </Typography.Text>
      </section>
    </SelfCampaignPageLayout>
  );
};

export default SelfCampaignDesign;
